// piano-frontend/src/components/NotificationBell.js
// 회원 전용 범용 인앱 알림함 — 빈자리(예약 가능)·레슨 승인/반려·취소 승인/반려 등
// Push로만 오던 소식을 헤더 종 아이콘에서 모아 볼 수 있게 한다.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, Badge, Popover, Button, Divider,
  CircularProgress, Avatar, Fade
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  NotificationsNoneRounded, NotificationsActiveRounded, NotificationsOffRounded,
  NotificationsRounded, EventAvailableRounded, CheckCircleRounded, CancelRounded,
  CloseRounded, ChevronRightRounded
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api/axios';
import { useDialog } from '../contexts/DialogProvider';

// 타입별 아이콘/색상 (MUI 팔레트 토큰 → 라이트·다크 모두 대응)
const TYPE_STYLE = {
  lesson_available:      { icon: EventAvailableRounded, color: 'primary' },
  lesson_approved:       { icon: CheckCircleRounded,    color: 'success' },
  lesson_rejected:       { icon: CancelRounded,         color: 'error' },
  cancellation_approved: { icon: CheckCircleRounded,    color: 'success' },
  cancellation_rejected: { icon: CancelRounded,         color: 'error' },
  default:               { icon: NotificationsRounded,  color: 'primary' },
};

function typeStyle(type) {
  return TYPE_STYLE[type] || TYPE_STYLE.default;
}

// created_at → '방금 전 / N분 전 / N시간 전 / N일 전 / M월 D일'
function formatRelative(iso) {
  if (!iso) return '';
  const now = dayjs();
  const then = dayjs(iso);
  if (!then.isValid()) return '';
  const min = now.diff(then, 'minute');
  if (min < 1) return '방금 전';
  if (min < 60) return `${min}분 전`;
  const hr = now.diff(then, 'hour');
  if (hr < 24) return `${hr}시간 전`;
  const day = now.diff(then, 'day');
  if (day < 7) return `${day}일 전`;
  return then.format('M월 D일');
}

const POLL_INTERVAL = 60 * 1000;

function NotificationBell() {
  const navigate = useNavigate();
  const { toast } = useDialog();
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);
  const open = Boolean(anchorEl);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      if (mountedRef.current) setUnreadCount(res.data?.unreadCount || 0);
    } catch {
      /* 뱃지 폴링 실패는 조용히 무시 */
    }
  }, []);

  // 미확인 개수 폴링 (마운트 + 포커스 + 주기, 탭이 보일 때만)
  useEffect(() => {
    mountedRef.current = true;
    fetchUnreadCount();

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    }, POLL_INTERVAL);
    const onFocus = () => fetchUnreadCount();
    window.addEventListener('focus', onFocus);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchUnreadCount]);

  const handleOpen = async (e) => {
    setAnchorEl(e.currentTarget);
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setItems(res.data?.items || []);
      if ((res.data?.unreadCount || 0) > 0) {
        api.post('/notifications/read-all').catch(() => {}); // 열람 시 읽음 처리
      }
      setUnreadCount(0);
    } catch {
      toast('알림을 불러오지 못했습니다.', 'error');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleClickItem = (item) => {
    if (!item.link) return;
    handleClose();
    const d = item.data || {};
    let state;
    if (item.type === 'lesson_available' && d.date) {
      // 빈자리 알림: 대기했던 날짜+시간까지 자동 선택하도록 예약 의도 플래그 전달
      state = { focusDate: d.date, focusTime: d.time, focusReserve: true };
    } else if (item.type === 'lesson_approved' && d.date) {
      // 레슨 승인: 내정보의 확정된 레슨으로 포커싱
      state = { focusLesson: { date: d.date, time: d.time } };
    } else if (item.type === 'lesson_rejected' || item.type === 'cancellation_rejected') {
      // 반려: 내정보의 반려된 신청으로 포커싱
      state = { focusRejected: true };
    } else if (d.date) {
      state = { focusDate: d.date };
    }
    navigate(item.link, state ? { state } : undefined);
  };

  const handleDismiss = async (e, id) => {
    e.stopPropagation();
    setItems(prev => prev.filter(i => i.id !== id));
    try {
      await api.delete(`/notifications/${id}`);
    } catch {
      toast('알림을 삭제하지 못했습니다.', 'error');
    }
  };

  const handleClearAll = async () => {
    setItems([]);
    try {
      await api.delete('/notifications/all');
    } catch {
      /* 실패해도 목록은 비워짐 */
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        aria-label="알림"
        sx={{ bgcolor: open ? 'action.hover' : 'transparent' }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={9}
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              minWidth: 16,
              height: 16,
              fontWeight: 700,
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsActiveRounded
              sx={{
                color: 'text.primary',
                animation: 'bell-swing 2.2s ease-in-out infinite',
                transformOrigin: 'top center',
                '@keyframes bell-swing': {
                  '0%, 60%, 100%': { transform: 'rotate(0deg)' },
                  '70%': { transform: 'rotate(12deg)' },
                  '80%': { transform: 'rotate(-10deg)' },
                  '90%': { transform: 'rotate(6deg)' },
                },
              }}
            />
          ) : (
            <NotificationsNoneRounded sx={{ color: 'text.secondary' }} />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            width: { xs: 'calc(100vw - 24px)', sm: 380 },
            maxWidth: 380,
            maxHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* 헤더 */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            알림
          </Typography>
          {items.length > 0 && (
            <Button
              size="small"
              onClick={handleClearAll}
              sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 0, px: 1 }}
            >
              모두 지우기
            </Button>
          )}
        </Box>
        <Divider />

        {/* 본문 */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={26} />
            </Box>
          ) : items.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 6,
                px: 3,
                color: 'text.secondary',
              }}
            >
              <NotificationsOffRounded sx={{ fontSize: 44, opacity: 0.35, mb: 1.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                새로운 알림이 없어요
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                빈자리·레슨 승인 등 소식이 생기면
                <br />
                이곳에서 알려드릴게요.
              </Typography>
            </Box>
          ) : (
            items.map((item, idx) => {
              const unread = !item.read_at;
              const { icon: Icon, color } = typeStyle(item.type);
              const clickable = Boolean(item.link);
              return (
                <Box key={item.id}>
                  {idx > 0 && <Divider component="div" sx={{ mx: 2 }} />}
                  <Box
                    onClick={() => clickable && handleClickItem(item)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.75,
                      cursor: clickable ? 'pointer' : 'default',
                      transition: 'background-color 0.15s',
                      bgcolor: unread
                        ? (theme) => alpha(theme.palette[color].main, 0.06)
                        : 'transparent',
                      '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
                    }}
                  >
                    {/* 미확인 좌측 액센트 바 */}
                    {unread && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 10,
                          bottom: 10,
                          width: 3,
                          borderRadius: 3,
                          bgcolor: `${color}.main`,
                        }}
                      />
                    )}

                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: (theme) => alpha(theme.palette[color].main, 0.12),
                        color: `${color}.main`,
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Avatar>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                          {item.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDismiss(e, item.id)}
                          sx={{ mt: -0.5, mr: -0.5, color: 'text.disabled' }}
                          aria-label="알림 삭제"
                        >
                          <CloseRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      {item.body && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, whiteSpace: 'pre-line' }}>
                          {item.body}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mt: 0.75,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          {formatRelative(item.created_at)}
                        </Typography>
                        {clickable && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: `${color}.main` }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              바로가기
                            </Typography>
                            <ChevronRightRounded sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
}

export default NotificationBell;
